import { BooleanInput, coerceBooleanProperty } from "@angular/cdk/coercion";
import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output } from "@angular/core";
import { FormControl, FormGroup } from "@angular/forms";
import { IconName } from "@dtm-frontend/shared/ui";
import { LocalComponentStore } from "@dtm-frontend/shared/utils";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { Subject } from "rxjs";
import { MapLayer } from "../map-controls.models";

interface MapLayersSelectorComponentState {
    mapLayers: ControllableMapLayer[];
    isTrackingReceiversSwitchVisible: boolean;
}

const mapLayerIconsMap: Record<string, IconName> = {
    [MapLayer.SoraBoxes]: "building",
    [MapLayer.Obstacles]: "barricade-fill",
};

const CONTROLLABLE_MAP_LAYERS = [
    MapLayer.SoraBoxes,
    MapLayer.Obstacles,
    MapLayer.AirwaySegments,
    MapLayer.AirwaySegmentsConnections,
    MapLayer.Default,
] as const;

type ControllableMapLayer = (typeof CONTROLLABLE_MAP_LAYERS)[number];

@UntilDestroy()
@Component({
    selector: "dtm-map-layers-selector[mapLayers]]",
    templateUrl: "./map-layers-selector.component.html",
    styleUrls: ["./map-layers-selector.component.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [LocalComponentStore],
})
export class MapLayersSelectorComponent implements OnInit {
    @Input({
        transform: (value: MapLayer[]): ControllableMapLayer[] =>
            value.filter((layer) => CONTROLLABLE_MAP_LAYERS.some((mapLayer) => mapLayer === layer)) as ControllableMapLayer[],
    })
    public set mapLayers(value: ControllableMapLayer[]) {
        this.localStore.patchState({ mapLayers: value });
        this.updateControlsState(value);
    }

    @Input() public set mapLayersValue(value: Partial<Record<MapLayer, boolean>>) {
        this.layersVisibilityForm.patchValue(value, { emitEvent: false });
    }

    @Input() public set isTrackingReceiversSwitchEnabled(value: BooleanInput | undefined) {
        this.localStore.patchState({ isTrackingReceiversSwitchVisible: value !== undefined });

        this.trackingReceiversSwitchControl.setValue(coerceBooleanProperty(value), { emitEvent: false });
    }

    protected readonly layersVisibilityForm = new FormGroup({
        [MapLayer.SoraBoxes]: new FormControl(false, { nonNullable: true }),
        [MapLayer.Obstacles]: new FormControl(false, { nonNullable: true }),
        [MapLayer.AirwaySegments]: new FormControl(false, { nonNullable: true }),
        [MapLayer.AirwaySegmentsConnections]: new FormControl(false, { nonNullable: true }),
        [MapLayer.Default]: new FormControl(false, { nonNullable: true }),
    });
    protected readonly trackingReceiversSwitchControl = new FormControl(false, { nonNullable: true });

    @Output() public readonly mapLayersValueChange = new Subject<Partial<Record<MapLayer, boolean>>>();
    @Output() public readonly trackingReceiversValueChange = new EventEmitter<boolean>();

    protected readonly mapLayers$ = this.localStore.selectByKey("mapLayers");
    protected readonly isTrackingReceiversSwitchVisible$ = this.localStore.selectByKey("isTrackingReceiversSwitchVisible");

    protected readonly MapLayer = MapLayer;

    constructor(private readonly localStore: LocalComponentStore<MapLayersSelectorComponentState>) {
        localStore.setState({
            mapLayers: [],
            isTrackingReceiversSwitchVisible: false,
        });
        this.watchAndUpdateFormState();
    }

    public ngOnInit(): void {
        this.layersVisibilityForm.updateValueAndValidity();
    }

    protected getIconForMapLayerType(layerName: string): IconName {
        return mapLayerIconsMap[layerName] ?? "map";
    }

    protected turnOffControl(value: boolean, control: ControllableMapLayer) {
        if (value) {
            this.layersVisibilityForm.controls[control].setValue(false);
        }
    }

    protected hasAirwaySegmentsLayer(mapLayers: ControllableMapLayer[]) {
        return mapLayers.includes(MapLayer.AirwaySegments);
    }

    protected hasAirwaySegmentsConnectionsLayer(mapLayers: ControllableMapLayer[]) {
        return mapLayers.includes(MapLayer.AirwaySegmentsConnections);
    }

    private watchAndUpdateFormState() {
        this.layersVisibilityForm.valueChanges.pipe(untilDestroyed(this)).subscribe((value) => {
            if (
                !value[MapLayer.SoraBoxes] &&
                !value[MapLayer.Obstacles] &&
                !value[MapLayer.AirwaySegments] &&
                !value[MapLayer.AirwaySegmentsConnections]
            ) {
                this.layersVisibilityForm.controls[MapLayer.Default].setValue(true, { emitEvent: false });
            }

            this.mapLayersValueChange.next(this.layersVisibilityForm.value);
        });

        this.trackingReceiversSwitchControl.valueChanges.pipe(untilDestroyed(this)).subscribe((value) => {
            this.trackingReceiversValueChange.emit(value);
        });
    }

    private updateControlsState(value: ControllableMapLayer[]) {
        Object.keys(MapLayer).forEach((layer) => {
            const typedControlName = layer as ControllableMapLayer;
            if (!value.includes(typedControlName)) {
                this.layersVisibilityForm.controls[typedControlName]?.disable({ emitEvent: false });
            } else {
                this.layersVisibilityForm.controls[typedControlName]?.enable({ emitEvent: false });
            }
        });

        this.layersVisibilityForm.updateValueAndValidity();
    }
}
